/**
 * A negotiated discount on one workshop's package price.
 *
 * Set here and nowhere else — the workshop sees the result on their own plan
 * screen but can never change it. An end date is optional; once it passes the
 * discount simply stops applying, so nobody has to remember to clear it.
 */
import React, { useState } from 'react';
import { Loader2, Pencil, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../../../lib/useApiFeedback';

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

export const DiscountCell = ({ tenant, plans, onSave }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    discount_percent: tenant.discount_percent || 0,
    discount_note: tenant.discount_note || '',
    discount_until: tenant.discount_until || '',
  });

  const plan = plans.find((p) => p.key === tenant.plan);
  const cycle = tenant.billing_cycle === 'yearly' ? 'yearly' : 'monthly';
  const list = cycle === 'yearly' ? plan?.price_yearly : plan?.price_monthly;
  const pct = Number(tenant.discount_percent || 0);
  const expired = tenant.discount_until
    && tenant.discount_until < new Date().toISOString().slice(0, 10);
  const payable = pct > 0 ? Math.max(1, Math.round(list * (100 - pct) / 100)) : list;

  const openEditor = () => {
    setForm({
      discount_percent: tenant.discount_percent || 0,
      discount_note: tenant.discount_note || '',
      discount_until: tenant.discount_until || '',
    });
    setOpen(true);
  };

  const save = async (clear) => {
    const pctIn = Number(form.discount_percent) || 0;
    if (!clear && (pctIn < 0 || pctIn > 90)) {
      toast.error('A discount has to be between 0% and 90%.');
      return;
    }
    setSaving(true);
    try {
      await onSave(tenant.id, clear
        ? { discount_percent: 0 }
        : {
          discount_percent: Number(form.discount_percent) || 0,
          discount_note: form.discount_note,
          discount_until: form.discount_until,
        });
      setOpen(false);
      toast.success(clear ? 'Discount removed' : 'Discount saved');
    } catch (e) {
      toast.error(formatApiError(e) || 'Could not save that discount');
    } finally { setSaving(false); }
  };

  if (!open) {
    return (
      <button
        onClick={openEditor}
        className="text-left group"
        data-testid={`platform-discount-${tenant.slug}`}
      >
        {pct > 0 ? (
          <>
            <div className={`text-sm font-semibold ${expired ? 'text-slate-400' : 'text-emerald-700'}`}>
              {pct}% off
            </div>
            <div className="text-[11px] text-slate-400">
              {expired
                ? `expired ${tenant.discount_until}`
                : (
                  <>
                    {money(payable)}/{cycle === 'yearly' ? 'yr' : 'mo'} ·{' '}
                    <span className="line-through">{money(list)}</span>
                  </>
                )}
            </div>
          </>
        ) : (
          <span className="text-xs text-slate-400 group-hover:text-teal-700 inline-flex items-center gap-1">
            <Pencil size={11} /> Set discount
          </span>
        )}
      </button>
    );
  }

  return (
    <div className="space-y-1.5 min-w-[12rem]" data-testid={`platform-discount-form-${tenant.slug}`}>
      <div className="flex items-center gap-1.5">
        <input
          type="number"
          min="0"
          max="90"
          step="0.5"
          value={form.discount_percent}
          onChange={(e) => setForm((f) => ({ ...f, discount_percent: e.target.value }))}
          className="w-16 border border-slate-300 rounded-lg px-2 py-1 text-sm"
          data-testid={`discount-percent-${tenant.slug}`}
        />
        <span className="text-xs text-slate-500">% off</span>
        <span className="text-[11px] text-slate-400 ml-auto tabular-nums">
          {money(Math.max(1, Math.round(list * (100 - (Number(form.discount_percent) || 0)) / 100)))}
        </span>
      </div>
      <input
        value={form.discount_note}
        onChange={(e) => setForm((f) => ({ ...f, discount_note: e.target.value }))}
        placeholder="Why, e.g. launch partner"
        className="w-full border border-slate-300 rounded-lg px-2 py-1 text-xs"
        data-testid={`discount-note-${tenant.slug}`}
      />
      <input
        type="date"
        value={form.discount_until}
        onChange={(e) => setForm((f) => ({ ...f, discount_until: e.target.value }))}
        title="Runs until (optional)"
        className="w-full border border-slate-300 rounded-lg px-2 py-1 text-xs"
        data-testid={`discount-until-${tenant.slug}`}
      />
      <div className="flex items-center gap-1.5">
        <button
          onClick={() => save(false)}
          disabled={saving}
          className="inline-flex items-center gap-1 text-xs font-semibold bg-teal-500 hover:bg-teal-600 text-white rounded-lg px-2.5 py-1"
          data-testid={`discount-save-${tenant.slug}`}
        >
          {saving ? <Loader2 className="animate-spin" size={11} /> : <Check size={11} />} Save
        </button>
        {pct > 0 && (
          <button
            onClick={() => save(true)}
            disabled={saving}
            className="text-xs font-semibold text-slate-500 hover:text-red-600"
            data-testid={`discount-clear-${tenant.slug}`}
          >
            Remove
          </button>
        )}
        <button onClick={() => setOpen(false)} className="ml-auto text-slate-400 hover:text-slate-600">
          <X size={13} />
        </button>
      </div>
    </div>
  );
};

export default DiscountCell;
